import { useEffect, useState } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { QrCode } from 'lucide-react'
import { api } from '@/lib/api'
import { useAuth } from '@/context/AuthContext'
import { Button, Card, EmptyState, Spinner } from '@/components/ui'
import { useT } from '@/i18n/I18nProvider'
import type { WorkCenter } from '@/types/api'

/**
 * Landing page for a scanned QR sticker.
 *
 * The printed stickers encode `{FRONTEND_URL}/qr/{token}`, so scanning one with
 * the phone's built-in camera — which is what most people do — opens this route
 * directly rather than requiring the app be opened first.
 *
 * The token is resolved server side and the user is forwarded to a submission
 * form with the work center already chosen.
 */
export function QrRedirectPage() {
  const { token } = useParams<{ token: string }>()
  const { user, isLoading } = useAuth()
  const t = useT()

  const [workCenterId, setWorkCenterId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!token || !user) return

    api
      .get<{ data: WorkCenter }>(`/qr/${token}`)
      .then(({ data }) => setWorkCenterId(data.data.id))
      .catch(() => setError(t('tasks.qr.unknownCode')))
  }, [token, user, t])

  if (isLoading) return <Spinner label={t('tasks.qr.checkingSession')} />

  /*
   * Not signed in: send them to login, remembering where they were going.
   * A worker who scans a sticker on a phone that has been logged out should end
   * up at the right form after logging in, not back at the home page.
   */
  if (!user) {
    return <Navigate to={`/prijava?nastavi=${encodeURIComponent(`/qr/${token}`)}`} replace />
  }

  if (error) {
    return (
      <Card>
        <EmptyState
          icon={<QrCode className="size-8" />}
          title={t('tasks.qr.unknownTitle')}
          description={error}
          action={
            <Button onClick={() => (window.location.href = '/prijave/nova')}>
              {t('tasks.scan.pickManually')}
            </Button>
          }
        />
      </Card>
    )
  }

  if (workCenterId) {
    return <Navigate to={`/prijave/nova?radniCentar=${workCenterId}`} replace />
  }

  return <Spinner label={t('tasks.qr.opening')} />
}
